import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  HostListener,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subject } from 'rxjs';

import { ClosingReportHelpModalComponent } from '../../../reports/closing-report/closing-report-help-modal/closing-report-help-modal.component';
import { ConfirmModalComponent } from './confirm-modal/confirm-modal.component';
import { DashboardSettingsModalComponent } from './dashboard-goal-settings-modal/dashboard-goal-settings-modal.component';
import { DeleteModalComponent } from './delete-modal/delete-modal.component';
import { DownloadExceptionModalComponent } from './download-exception-modal/download-exception-modal.component';
import { FdmBlackBoxModalComponent } from './fdm-black-box-modal/fdm-black-box-modal.component';
import { GiftcardHelpModalComponent } from '../../../payments/giftcards/giftcard-help-modal/giftcard-help-modal.component';
import { GiftcardSettingsComponent } from './giftcard-settings/giftcard-settings.component';
import { MessageModalComponent } from './message-modal/message-modal.component';
import { PrintersHelpModalComponent } from '../../../printing/printers/printers-help-modal/printers-help-modal.component';
import { ShiftSettingModalComponent } from './shift-setting/shift-setting-modal.component';

import { ModalService } from './modal.service';

import { KeyCodes } from '../../keylistener/key-code.enum';
import { Modals } from './modals';
import { TimezoneModalComponent } from './timezone-modal/timezone-modal.component';
import { ImportModalComponent } from './import-modal/import-modal.component';

@Component({
  selector: 'ls-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  exportAs: 'lsModal',
  entryComponents: [
    ClosingReportHelpModalComponent,
    ConfirmModalComponent,
    DashboardSettingsModalComponent,
    DeleteModalComponent,
    DownloadExceptionModalComponent,
    FdmBlackBoxModalComponent,
    GiftcardHelpModalComponent,
    GiftcardSettingsComponent,
    MessageModalComponent,
    PrintersHelpModalComponent,
    ShiftSettingModalComponent,
    TimezoneModalComponent,
    ImportModalComponent
  ],
  animations: [
    trigger('modal', [
      transition('void => *', [style({ opacity: '1' }), animate(300)]),
      transition('* => void', [animate(300, style({ opacity: '0' }))])
    ])
  ]
})
export class ModalComponent implements OnInit {
  public isOpen = false;

  private currentModalComponent: ComponentRef<Modals>;
  private _dynamicComponentContainer: ViewContainerRef;
  private modalClosed$: Subject<any>;
  private queue: Array<ScheduledModal> = [];
  private scheduledModals: Array<ScheduledModal> = [];

  constructor(
    private modal: ModalService,
    private componentFactoryResolver: ComponentFactoryResolver
  ) {}

  public ngOnInit(): void {
    this.modal.registerModal(this);
  }

  public get dynamicComponentContainer(): ViewContainerRef {
    return this._dynamicComponentContainer;
  }

  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef })
  public set dynamicComponentContainer(value: ViewContainerRef) {
    this._dynamicComponentContainer = value;
    this.loadScheduledComponents();
  }

  public open(component: Type<Modals>, inputs: any): Subject<any> {
    const subject: Subject<any> = new Subject<any>();
    this.scheduleComponent({ component, inputs, subject });
    return subject;
  }

  public close(data?: any): void {
    if (data !== undefined && data !== null) {
      this.modalClosed$.next(data);
    }

    if (this.queue.length > 0) {
      const queuedModal: ScheduledModal = this.queue.pop();
      this.modalClosed$ = queuedModal.subject;
      const componentFactory: ComponentFactory<
        Modals
      > = this.componentFactoryResolver.resolveComponentFactory(
        queuedModal.component
      );
      this.dynamicComponentContainer.clear();
      this.currentModalComponent = this.dynamicComponentContainer.createComponent(
        componentFactory
      );
      (<ModalComponentData>this.currentModalComponent.instance).data =
        queuedModal.inputs;
    } else {
      this.isOpen = false;
      this.modalClosed$ = null;
      if (this.currentModalComponent) {
        this.currentModalComponent.destroy();
        this.currentModalComponent = null;
      }
    }
  }

  @HostListener('window:keyup', ['$event'])
  public handleKeyboard(event: KeyboardEvent): void {
    // tslint:disable-next-line
    if (this.dynamicComponentContainer && event.keyCode === KeyCodes.ESCAPE) {
      this.close(null);
      event.preventDefault();
    }
  }

  private loadModal(scheduledModal: ScheduledModal): void {
    if (this.dynamicComponentContainer) {
      const componentFactory: ComponentFactory<
        Modals
      > = this.componentFactoryResolver.resolveComponentFactory(
        scheduledModal.component
      );
      this.dynamicComponentContainer.clear();
      const modalComponent: ComponentRef<
        Modals
      > = this.dynamicComponentContainer.createComponent(componentFactory);
      (<ModalComponentData>modalComponent.instance).data =
        scheduledModal.inputs;
      modalComponent.changeDetectorRef.detectChanges();

      if (this.currentModalComponent) {
        this.queue.push(scheduledModal);
      }

      this.currentModalComponent = modalComponent;
      this.modalClosed$ = scheduledModal.subject;
    }
  }

  private scheduleComponent(modalComponent: ScheduledModal): void {
    this.scheduledModals.push(modalComponent);
    // if isOpen was already true, a modal was already open, and we just can load the other component on top of it
    // if not we set the isOpen and we wait until the container ref is active
    if (this.dynamicComponentContainer) {
      this.loadScheduledComponents();
    } else {
      this.isOpen = true;
    }
  }

  private loadScheduledComponents(): void {
    let scheduledModal: ScheduledModal = this.scheduledModals.shift();
    while (scheduledModal) {
      this.loadModal(scheduledModal);
      scheduledModal = this.scheduledModals.shift();
    }
  }
}

interface ScheduledModal {
  component: Type<Modals>;
  inputs: any;
  subject: Subject<any>;
}

interface ModalComponentData {
  data: any;
}
